/**
 * Prescribers page
 *
 * How many prescriptions each prescriber wrote in a date range.
 *
 * Two sources sit behind one table: each core clinic's `scripts` collection,
 * and embedded orders for MDExam. Every row is labeled with its basis so the
 * two are never silently added together.
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { prescriberAPI } from '../services/api'
import { useAuthStore } from '../stores/authStore'
import { DateRangePicker } from '../components/DateRangePicker'
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription, Button, Badge, Input,
} from '../components/ui'
import { AlertTriangle, Info, Loader2, Search, X } from 'lucide-react'

const BASIS_LABEL = {
  core_scripts: 'Clinic scripts',
  dosespot_prescriptions: 'DoseSpot prescriptions',
  legacy_script_written: 'Legacy scripts',
}

const BASIS_SHORT = {
  core_scripts: 'Scripts',
  dosespot_prescriptions: 'DoseSpot',
  legacy_script_written: 'Legacy',
}

const EXPRESS_SCOPE = '__express__'

const TIMEZONES = [
  { id: 'America/New_York', label: 'Eastern' },
  { id: 'America/Chicago', label: 'Central' },
  { id: 'America/Denver', label: 'Mountain' },
  { id: 'America/Phoenix', label: 'Arizona' },
  { id: 'America/Los_Angeles', label: 'Pacific' },
  { id: 'America/Anchorage', label: 'Alaska' },
  { id: 'Pacific/Honolulu', label: 'Hawaii' },
]

const DEFAULT_TIMEZONE = 'America/New_York'

const COUNTED_TWICE_TITLE =
  'This doctor wrote prescriptions for more than one clinic in this report (for example MDExam and another brand). Each clinic row is correct on its own. If you add those rows together, or use the total at the top, this person is counted twice.'

function formatNumber(value) {
  return (value || 0).toLocaleString()
}

function toDateParam(date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function formatWritten(script, timeZone = DEFAULT_TIMEZONE) {
  const raw = script.script_written_at || script.prescribed_at
  if (raw == null || raw === '') return '—'
  const date = new Date(raw)
  if (Number.isNaN(date.getTime())) return String(raw)
  return date.toLocaleString('en-US', { timeZone })
}

export default function PrescribersPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { clinics, user } = useAuthStore()
  const isSuperAdmin = user?.role === 'super_admin'
  const canViewThisReport = isSuperAdmin || Boolean(user?.permissions?.canViewPrescribers)

  const [dateRange, setDateRange] = useState(() => {
    const start = searchParams.get('startDate')
    const end = searchParams.get('endDate')
    if (start && end) return { from: new Date(`${start}T12:00:00`), to: new Date(`${end}T12:00:00`) }
    const to = new Date()
    const from = new Date()
    from.setDate(from.getDate() - 29)
    return { from, to }
  })

  const [filterClinicId, setFilterClinicId] = useState(() => {
    if (searchParams.get('scope') === 'express') return EXPRESS_SCOPE
    const clinic = searchParams.get('clinic')
    if (clinic) return clinic
    return isSuperAdmin ? '' : EXPRESS_SCOPE
  })
  const [includeZero, setIncludeZero] = useState(searchParams.get('includeZero') === '1')
  const [timezone, setTimezone] = useState(() => {
    const requested = searchParams.get('timezone')
    return TIMEZONES.some(zone => zone.id === requested) ? requested : DEFAULT_TIMEZONE
  })
  const [search, setSearch] = useState('')
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [detail, setDetail] = useState(null)
  const [detailLoading, setDetailLoading] = useState(false)

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = {
        startDate: toDateParam(dateRange.from),
        endDate: toDateParam(dateRange.to),
        timezone,
      }
      if (filterClinicId === EXPRESS_SCOPE) params.scope = 'express'
      else if (filterClinicId) params.clinicId = filterClinicId
      if (includeZero) params.includeZero = '1'

      const res = await prescriberAPI.getReport(params)
      if (!res.data.success) throw new Error(res.data.error?.message || 'Failed to load')
      setData(res.data.data)
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message)
    } finally {
      setLoading(false)
    }
  }, [dateRange, filterClinicId, includeZero, timezone])

  useEffect(() => { fetchData() }, [fetchData])

  useEffect(() => {
    const params = new URLSearchParams()
    params.set('startDate', toDateParam(dateRange.from))
    params.set('endDate', toDateParam(dateRange.to))
    if (filterClinicId === EXPRESS_SCOPE) params.set('scope', 'express')
    else if (filterClinicId) params.set('clinic', filterClinicId)
    if (includeZero) params.set('includeZero', '1')
    if (timezone !== DEFAULT_TIMEZONE) params.set('timezone', timezone)
    setSearchParams(params, { replace: true })
  }, [dateRange, filterClinicId, includeZero, timezone, setSearchParams])

  const openDetail = useCallback(async (prescriber) => {
    if (!prescriber.prescriber_id) return
    setDetail({ prescriber, scripts: [], count: 0 })
    setDetailLoading(true)
    try {
      const params = {
        startDate: toDateParam(dateRange.from),
        endDate: toDateParam(dateRange.to),
        timezone,
      }
      const res = prescriber.basis === 'legacy_script_written'
        ? await prescriberAPI.getLegacyScripts(prescriber.prescriber_id, params)
        : await prescriberAPI.getCoreScripts(prescriber.clinicId, prescriber.prescriber_id, params)
      if (res.data.success) setDetail({ prescriber, ...res.data.data })
    } catch (err) {
      setDetail({ prescriber, error: err.response?.data?.error?.message || err.message, scripts: [] })
    } finally {
      setDetailLoading(false)
    }
  }, [dateRange, timezone])

  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase()
    if (!needle) return data?.prescribers || []
    return (data?.prescribers || []).filter(row =>
      [row.name, row.npi, row.prescriber_id, row.dosespot_id, row.displayName]
        .some(field => String(field || '').toLowerCase().includes(needle))
    )
  }, [data, search])

  const mixedBases = useMemo(() => {
    const bases = new Set((data?.clinics || []).map(c => c.basis))
    return bases.size > 1
  }, [data])

  const overlapping = useMemo(() => {
    const ids = new Set()
    for (const entry of data?.overlapping_prescribers || []) {
      if (entry.doctor_id) ids.add(String(entry.doctor_id))
      if (entry.dosespot_id) ids.add(String(entry.dosespot_id))
      if (entry.npi) ids.add(String(entry.npi))
    }
    return ids
  }, [data])

  const clinicOptions = useMemo(() => {
    if (isSuperAdmin) return clinics || []
    return (data?.clinics || []).map(clinic => ({
      id: clinic.clinicId,
      displayName: clinic.displayName,
    }))
  }, [isSuperAdmin, clinics, data])

  const isOverlapping = (prescriber) =>
    overlapping.size > 0 &&
    (overlapping.has(String(prescriber.prescriber_id)) ||
      overlapping.has(String(prescriber.dosespot_id)) ||
      overlapping.has(String(prescriber.npi)))

  if (user && !canViewThisReport) {
    return (
      <div className="container mx-auto px-4 py-6">
        <p className="text-muted-foreground">You do not have access to the Prescribers report.</p>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-6 space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Prescribers</h1>
          <p className="text-muted-foreground mt-1">
            Prescriptions written per prescriber
            {data?.scope === 'express' && ' · Express clinics'}
            {data?.range && (
              <> &middot; {data.range.start_date} to {data.range.end_date} ({data.range.days} days, {data.range.timezone})</>
            )}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={filterClinicId}
            onChange={(e) => setFilterClinicId(e.target.value)}
            className="text-sm border rounded-md px-3 py-2 bg-background text-foreground"
          >
            {isSuperAdmin && <option value="">All my clinics</option>}
            <option value={EXPRESS_SCOPE}>All Express clinics</option>
            {clinicOptions.map(clinic => (
              <option key={clinic.id} value={clinic.id}>{clinic.displayName || clinic.name || clinic.id}</option>
            ))}
          </select>
          <DateRangePicker dateRange={dateRange} onDateRangeChange={setDateRange} />
          <select
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            title="Which clock to use for the start and end of each calendar day. Default Eastern. We do not store the doctor's timezone — use this when checking a dispute in their local time."
            className="text-sm border rounded-md px-3 py-2 bg-background text-foreground"
          >
            {TIMEZONES.map(zone => (
              <option key={zone.id} value={zone.id}>{zone.label}</option>
            ))}
          </select>
          <Button variant="outline" onClick={fetchData} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Refresh'}
          </Button>
        </div>
      </div>

      {error && (
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <p className="text-destructive text-sm">{error}</p>
          </CardContent>
        </Card>
      )}

      {data?.summary && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {mixedBases ? 'Prescriptions (mixed basis)' : 'Prescriptions'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <span className="text-2xl font-bold tabular-nums">{formatNumber(data.summary.total_prescriptions)}</span>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Prescribers with volume</CardTitle>
            </CardHeader>
            <CardContent>
              <span className="text-2xl font-bold tabular-nums">{formatNumber(data.summary.prescribers)}</span>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Clinics</CardTitle>
            </CardHeader>
            <CardContent>
              <span className="text-2xl font-bold tabular-nums">{formatNumber(data.summary.clinics)}</span>
            </CardContent>
          </Card>
        </div>
      )}

      {data?.caveats?.length > 0 && (
        <Card className="border-amber-500/50 bg-amber-500/5">
          <CardContent className="pt-6 space-y-2">
            {data.caveats.map((caveat, i) => (
              <p key={i} className="text-sm text-amber-700 dark:text-amber-400 flex gap-2">
                <Info className="h-4 w-4 shrink-0 mt-0.5" />
                <span>{caveat}</span>
              </p>
            ))}
          </CardContent>
        </Card>
      )}

      {data?.unavailable?.length > 0 && (
        <Card className="border-destructive/50">
          <CardContent className="pt-6 space-y-1">
            {data.unavailable.map(entry => (
              <p key={entry.clinicId} className="text-sm text-destructive flex gap-2">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                <span><span className="font-medium">{entry.clinicId}</span> could not be read: {entry.error}</span>
              </p>
            ))}
          </CardContent>
        </Card>
      )}

      {data?.clinics?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>By clinic</CardTitle>
            <CardDescription>Which source produced each clinic&apos;s number</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-3 font-medium">Clinic</th>
                    <th className="text-right py-2 px-3 font-medium">Prescriptions</th>
                    <th className="text-right py-2 px-3 font-medium">Prescribers</th>
                    <th className="text-right py-2 px-3 font-medium">Roster</th>
                    <th className="text-left py-2 px-3 font-medium">Basis</th>
                    <th className="text-left py-2 px-3 font-medium">Attribution</th>
                  </tr>
                </thead>
                <tbody>
                  {data.clinics.map(clinic => (
                    <tr key={clinic.clinicId} className="border-b hover:bg-muted/50">
                      <td className="py-2 px-3">
                        <span className="inline-flex items-center gap-2">
                          <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: clinic.color }} />
                          <span className="font-medium">{clinic.displayName}</span>
                        </span>
                      </td>
                      <td className="py-2 px-3 text-right font-mono tabular-nums">{formatNumber(clinic.prescriptions)}</td>
                      <td className="py-2 px-3 text-right font-mono tabular-nums">{formatNumber(clinic.prescribers)}</td>
                      <td className="py-2 px-3 text-right font-mono tabular-nums text-muted-foreground">
                        {formatNumber(clinic.roster_size)}
                      </td>
                      <td className="py-2 px-3">
                        <Badge variant="secondary" className="text-xs">{BASIS_LABEL[clinic.basis] || clinic.basis}</Badge>
                      </td>
                      <td className="py-2 px-3 text-xs text-muted-foreground">
                        {clinic.attribution === 'scripts_collection' && 'scripts collection'}
                        {clinic.attribution === 'legacy_doctor_id' && 'legacy doctorId'}
                        {clinic.attribution === 'dedicated_clinic_id' && `DoseSpot clinic ${clinic.dosespot_clinic_id}`}
                        {clinic.attribution === 'roster_match' && 'roster match'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <CardTitle>Prescribers</CardTitle>
            <CardDescription>
              {rows.length === (data?.prescribers?.length || 0)
                ? `${formatNumber(rows.length)} prescriber${rows.length === 1 ? '' : 's'}`
                : `${formatNumber(rows.length)} of ${formatNumber(data?.prescribers?.length || 0)}`}
              {overlapping.size > 0 && (
                <>
                  {' · '}
                  A yellow “counted twice” tag means this doctor appears under more than one clinic.
                  Each clinic&apos;s number is right. Adding them together counts that person twice.
                </>
              )}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 text-sm text-muted-foreground whitespace-nowrap">
              <input
                type="checkbox"
                checked={includeZero}
                onChange={(e) => setIncludeZero(e.target.checked)}
                className="rounded border-input"
              />
              Show zero-volume
            </label>
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Name, NPI or id"
                className="pl-8 w-56"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading && !data ? (
            <div className="py-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : rows.length === 0 ? (
            <p className="py-12 text-center text-muted-foreground text-sm">
              No prescriptions found for this range.
              {!includeZero && ' Tick "Show zero-volume" to list the roster anyway.'}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-3 font-medium">Prescriber</th>
                    <th className="text-left py-2 px-3 font-medium">Clinic</th>
                    <th className="text-left py-2 px-3 font-medium">NPI</th>
                    <th className="text-right py-2 px-3 font-medium">Prescriptions</th>
                    <th className="text-right py-2 px-3 font-medium">Orders</th>
                    <th className="text-left py-2 px-3 font-medium">Basis</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((prescriber, index) => {
                    const legacy = prescriber.basis === 'legacy_script_written'
                    const clickable = Boolean(prescriber.prescriber_id)
                    return (
                      <tr
                        key={`${prescriber.clinicId}-${prescriber.prescriber_id || prescriber.dosespot_id || index}`}
                        className={`border-b ${clickable ? 'cursor-pointer hover:bg-muted/50' : 'hover:bg-muted/30'}`}
                        onClick={clickable ? () => openDetail(prescriber) : undefined}
                        title={clickable ? 'View the individual scripts' : undefined}
                      >
                        <td className="py-2 px-3">
                          <span className="font-medium">
                            {prescriber.name || (
                              <span className="text-muted-foreground italic">
                                {prescriber.prescriber_id === null ? 'No prescriber on record' : `Unknown (${prescriber.prescriber_id || prescriber.dosespot_id})`}
                              </span>
                            )}
                          </span>
                          {prescriber.not_in_roster && (
                            <Badge
                              variant="outline"
                              className="ml-2 text-xs"
                              title="Wrote prescriptions in this range but is not on the clinic's current roster — usually a departed prescriber"
                            >
                              off roster
                            </Badge>
                          )}
                          {isOverlapping(prescriber) && (
                            <Badge
                              variant="outline"
                              className="ml-2 text-xs border-amber-500 text-amber-600"
                              title={COUNTED_TWICE_TITLE}
                            >
                              counted twice
                            </Badge>
                          )}
                        </td>
                        <td className="py-2 px-3">
                          <span className="inline-flex items-center gap-2">
                            <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: prescriber.color }} />
                            <span className="text-muted-foreground">{prescriber.displayName}</span>
                          </span>
                        </td>
                        <td className="py-2 px-3 font-mono text-xs text-muted-foreground">{prescriber.npi || '—'}</td>
                        <td className="py-2 px-3 text-right font-mono tabular-nums font-medium">
                          {formatNumber(prescriber.prescriptions)}
                        </td>
                        <td className="py-2 px-3 text-right font-mono tabular-nums text-muted-foreground">
                          {legacy ? formatNumber(prescriber.orders) : '—'}
                        </td>
                        <td className="py-2 px-3">
                          <span className="text-xs text-muted-foreground">{BASIS_SHORT[prescriber.basis] || prescriber.basis}</span>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {detail && (
        <Card>
          <CardHeader className="flex flex-row items-start justify-between gap-4">
            <div>
              <CardTitle className="text-base">
                {detail.prescriber.name || detail.prescriber.prescriber_id} &middot; individual scripts
              </CardTitle>
              <CardDescription>
                {detailLoading
                  ? 'Loading…'
                  : `${formatNumber(detail.count || 0)} script${detail.count === 1 ? '' : 's'}${detail.truncated ? ' (truncated)' : ''} · use order IDs below to look a row up`}
              </CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setDetail(null)}>
              <X className="h-4 w-4" />
            </Button>
          </CardHeader>
          <CardContent>
            {detail.error ? (
              <p className="text-sm text-destructive">{detail.error}</p>
            ) : detailLoading ? (
              <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
            ) : (detail.scripts || []).length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">No scripts in this range.</p>
            ) : (
              <div className="overflow-x-auto max-h-96 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-background">
                    <tr className="border-b">
                      <th className="text-left py-2 px-3 font-medium">Patient</th>
                      <th className="text-left py-2 px-3 font-medium">State</th>
                      <th className="text-left py-2 px-3 font-medium">Drug</th>
                      <th className="text-left py-2 px-3 font-medium">Pharmacy</th>
                      <th className="text-left py-2 px-3 font-medium">Order ID</th>
                      <th className="text-left py-2 px-3 font-medium">Script ID</th>
                      <th className="text-left py-2 px-3 font-medium">Written</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.scripts.map((script, i) => (
                      <tr key={`${script.order_id || script.script_id || i}-${i}`} className="border-b hover:bg-muted/50">
                        <td className="py-2 px-3">
                          {script.patient_name || (script.dosespot_patient_id ? `DS ${script.dosespot_patient_id}` : '—')}
                          {script.patient_name && script.dosespot_patient_id && (
                            <div className="text-[10px] text-muted-foreground/80">DS {script.dosespot_patient_id}</div>
                          )}
                        </td>
                        <td className="py-2 px-3 text-muted-foreground">{script.patient_state || '—'}</td>
                        <td className="py-2 px-3 font-mono text-xs">{script.drug || '—'}</td>
                        <td className="py-2 px-3 text-muted-foreground">{script.pharmacy || '—'}</td>
                        <td className="py-2 px-3 font-mono text-xs text-muted-foreground" title={script.prescription_id ? `DoseSpot ${script.prescription_id}` : undefined}>
                          {script.order_id || '—'}
                          {script.prescription_id && (
                            <div className="text-[10px] text-muted-foreground/80">Rx {script.prescription_id}</div>
                          )}
                        </td>
                        <td className="py-2 px-3 font-mono text-xs text-muted-foreground">{script.script_id || script.customer_id || '—'}</td>
                        <td className="py-2 px-3 text-muted-foreground text-xs">
                          {formatWritten(script, timezone)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}
